import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Messages } from '../../../gen-src/Localize';
import { encodeSvg } from '../../icons/Icons';
import { IconFillColors } from '../../styles/Colors';
import { Chip, ChipProps } from './Chip';

// tslint:disable:max-line-length
const SuccessSvg = (fill: string) => `<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11">
<path fill="${fill}" d="M4.42528736,11 C4.17241379,11 3.91954023,10.8735632 3.79310345,10.7471264 L0,6.95402299 L1.3908046,5.56321839 L4.29885057,8.47126437 L9.35632184,0 L11,1.01149425 L5.31034483,10.6206897 C5.05747126,10.8735632 4.8045977,11 4.42528736,11 L4.42528736,11 Z"/>
</svg>
`;
const InfoSvg = (fill: string) => `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13">
<path fill="${fill}" d="M6.5,0 C10.1,0 13,2.9 13,6.5 C13,10.1 10.1,13 6.5,13 C2.9,13 0,10.1 0,6.5 C0,2.9 2.9,0 6.5,0 Z M5.9,2.9 L5.9,4.2 L7.2,4.2 L7.2,2.9 L5.9,2.9 Z M5.9,5 L5.9,10 L7.2,10 L7.2,5 L5.9,5 Z"/>
</svg>
`;
const WarningSvg = (fill: string) => `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14">
<path fill="${fill}" d="M12.4444444,14 L1.55555556,14 C0.7,14 0,13.3 0,12.4444444 L0,1.55555556 C0,0.7 0.7,0 1.55555556,0 L12.4444444,0 C13.3,0 14,0.7 14,1.55555556 L14,12.4444444 C14,13.3 13.3,14 12.4444444,14 Z M6.22222222,2.33333333 L6.22222222,8.55555556 L7.77777778,8.55555556 L7.77777778,2.33333333 L6.22222222,2.33333333 Z M6.22222222,10.1111111 L6.22222222,11.6666667 L7.77777778,11.6666667 L7.77777778,10.1111111 L6.22222222,10.1111111 Z"/>
</svg>
`;
const ErrorSvg = (fill: string) => `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="13">
<path fill="${fill}" d="M6.20631136,0.486631016 C6.57745685,-0.162210339 7.50532059,-0.162210339 7.78367971,0.486631016 L13.9075804,11.6096257 C14.1859395,12.258467 13.814794,13 13.072503,13 L0.917488052,13 C0.267983436,13 -0.195948433,12.258467 0.0824106882,11.6096257 L6.20631136,0.486631016 Z M6.39188411,3.73083779 L6.39188411,9.29233512 L7.78367971,9.29233512 L7.78367971,3.73083779 L6.39188411,3.73083779 Z M6.39188411,10.2192513 L6.39188411,11.1461676 L7.78367971,11.1461676 L7.78367971,10.2192513 L6.39188411,10.2192513 Z"/>
</svg>
`;

type FeedbackChipResource = { bgColor: string, hoverBgColor?: string, iconFill: string, icon: (fill: string) => string, iconWidth: number, getPrefix: () => string };
const ResourceMap: { [key in 'success' | 'info' | 'warning' | 'error']: FeedbackChipResource } = {
  success: { bgColor: '#ACE5C8', iconFill: '#034235', icon: SuccessSvg, iconWidth: 11, getPrefix: Messages.successToastPrefix },
  info: { bgColor: '#D7ECF9', iconFill: '#0F445E', icon: InfoSvg, iconWidth: 13, getPrefix: Messages.infoToastPrefix },
  warning: { bgColor: '#FBEDA4', iconFill: '#A84010', icon: WarningSvg, iconWidth: 14, getPrefix: Messages.warningToastPrefix },
  error: { bgColor: '#F8C7CB', hoverBgColor: '#F4A4AA', iconFill: '#C93A47', icon: ErrorSvg, iconWidth: 14, getPrefix: Messages.errorToastPrefix }
};
// tslint:enable:max-line-length

export interface FeedbackChipProps extends ChipProps {
  /** Rendered as the `aria-label` of the chip, along with the type prefix, e.g. `Error: ${message}`. */
  message: string;
}

/** Takes in a resource, returns a react function component for that type of chip. */
const FeedbackChipFactory = (resource: FeedbackChipResource) =>
  React.forwardRef<HTMLDivElement, FeedbackChipProps & React.HTMLAttributes<HTMLElement>>((props, ref) => {
    const { message, ...chipProps } = props;
    const ariaLabel = `${resource.getPrefix()}${message}`;
    return <Chip
      ref={ref}
      tooltip={message}
      {...chipProps}
      aria-label={ariaLabel}
      className={
        TypeStyle.classes(props.className, TypeStyle.style({
          backgroundColor: resource.bgColor,
          backgroundImage: `url(${encodeSvg(resource.icon(chipProps['aria-disabled'] ? IconFillColors.Disabled : resource.iconFill))})`,
          backgroundOrigin: 'border-box',
          backgroundPositionX: (24 - resource.iconWidth) / 2,
          backgroundPositionY: 'center',
          backgroundRepeat: 'no-repeat',
          paddingLeft: 24 - 1, // 24px icon area minus 1px border plus 4px spacing between icon and text
          $nest: { '&:hover': { backgroundColor: resource.hoverBgColor } }
        }))}
    />;
  });

// export const SuccessChip = FeedbackChipFactory(ResourceMap.success);
// SuccessChip.displayName = 'SuccessChip';
// export const InfoChip = FeedbackChipFactory(ResourceMap.info);
// InfoChip.displayName = 'InfoChip';
// export const WarningChip = FeedbackChipFactory(ResourceMap.warning);
// WarningChip.displayName = 'WarningChip';
export const ErrorChip = FeedbackChipFactory(ResourceMap.error);
ErrorChip.displayName = 'ErrorChip';
